/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import { BaseInputProps } from '../shared-types';
import { getClassNames, getGetIdFn, toPascalCase } from '../utils';
import useTheme from '../style/useTheme';
import { BaseCheckboxV2 } from './CheckboxV2';
import Typography from '../Typography/Typography';
import { TypographyVariant } from '../style/typography';

export type Props = Omit<BaseInputProps, 'onChange' | 'label'> &
  React.PropsWithChildren<{
    // Make `label` non-optional
    label: string;
    checked?: boolean;
    onChange: (value: boolean) => void;
    _focused?: boolean;
    _hovered?: boolean;
    subtitle?: string;
    typography?: TypographyVariant;
  }>;

const CheckboxField: React.FC<Props> = ({
  label,
  checked,
  onChange,
  disabled,
  'aria-label': ariaLabel,
  required,
  children,
  'data-testid': dataTestID,
  _focused = false,
  _hovered = false,
  subtitle,
  typography,
}) => {
  const { theme, resolveColor } = useTheme();
  const fieldId = useMemo(
    () => getGetIdFn('CheckboxField')(toPascalCase(dataTestID || label)),
    [dataTestID, label]
  );

  const checkboxId = useMemo(
    () => getGetIdFn('Checkbox')(toPascalCase(dataTestID || label)),
    [dataTestID, label]
  );

  const [focused, setFocused] = useState(_focused);
  const [hovered, setHovered] = useState(_hovered);

  const onClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const backgroundColor = useMemo(() => {
    if (hovered)
      return resolveColor({
        type: 'theme',
        colorLegacy:
          theme.components.checkbox.default.selectedBackground.legacy,
        opacityLegacy: 0.04,
        colorNext: theme.components.checkbox.default.focusedBackground.next,
        opacityNext:
          theme.components.checkbox.default.focusedBackground.opacityNext,
      });
    if (checked) {
      if (disabled) {
        return resolveColor({
          type: 'component',
          value: theme.components.checkbox.default.selectedBackground,
        });
      }
      return resolveColor({
        type: 'component',
        value: theme.components.checkbox.default.selectedBackground,
      });
    }
    if (focused)
      return resolveColor({
        type: 'component',
        value: theme.components.checkbox.default.focusedBackground,
      });
    return resolveColor({
      type: 'component',
      value: theme.components.checkbox.default.baseBackground,
    });
  }, [checked, disabled, focused, hovered, resolveColor, theme.components]);

  const subtitleTypographyVariant = useMemo(() => {
    if (typography === 'marquee') return 'header';
    if (typography === 'header') return 'body';
    if (typography === 'body') return 'caption';
    if (typography === 'label') return 'caption';
    if (typography === 'caption') return 'postscript';
    if (typography === 'postscript') return 'postscript';

    return 'caption';
  }, [typography]);

  return (
    <div
      onMouseOver={() => setHovered(_hovered !== undefined ? _hovered : true)}
      onMouseLeave={() => setHovered(_hovered !== undefined ? _hovered : false)}
      onFocus={() => setFocused(focused !== undefined ? _focused : true)}
      onBlur={() => setFocused(focused !== undefined ? _focused : false)}
      css={{
        opacity: disabled ? '30%' : 'initial',
        padding: '16px',
        display: 'flex',
        borderRadius: '8px',
        transition: `all ${theme.animations.duration.standard}ms ${theme.animations.easing.easeOut}`,
        flexDirection: 'column',
        alignItems: 'flex-start',
        cursor: 'pointer',
        backgroundColor,
        border: `${focused ? 2 : 0}px solid ${resolveColor({
          type: 'theme',
          colorLegacy: 'Primary/50',
          colorNext: 'brand/accent',
        })}`,
      }}
      data-testid={fieldId}
      className={getClassNames(
        { 'origami-Radio--selected': checked },
        'origami-Radio'
      )}
      onClick={() => {
        onClick();
      }}
    >
      <BaseCheckboxV2
        id={checkboxId}
        focused={focused}
        hovered={hovered}
        checked={!!checked}
        disabled={disabled}
        aria-label={ariaLabel}
        required={required}
        label={label}
        typography={typography}
      />
      {children}
      {subtitle ? (
        <Typography
          color="light"
          weight="normal"
          variant={subtitleTypographyVariant}
          sx={{ marginLeft: '32px' }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </div>
  );
};

export default CheckboxField;
